import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FaArrowDown,
  FaArrowUp,
  FaBox,
  FaChevronDown,
  FaChevronRight,
  FaEye,
  FaLayerGroup,
  FaSave,
  FaSearch,
  FaTimes,
} from "react-icons/fa";
import CommonService from "../../services/common.service";
import b2bHubHomeService from "../../services/b2bHubHome.service";
import {
  fetchHubHomeSections,
  hubHomeSectionExtractMessage,
  updateHubHomeSection,
} from "../../services/hubHomeSectionAdmin.service";
import {
  buildFeaturedCategoryShowcaseSectionConfig,
  HUB_HOME_SECTION_KEYS,
  resolveFeaturedCategoryShowcaseConfig,
  resolveSectionGridConfig,
} from "../../constants/hubHomeSections";
import { resolvePublicFileUrl } from "../../constants/api-urls";
import "../../assets/styles/master_category_admin.scss";
import "../../assets/styles/super_admin.scss";
import "../../assets/styles/super_admin_service_quick_links.scss";
import "../../assets/styles/super_admin_trading_categories.scss";
import "../../assets/styles/super_admin_featured_category_showcase.scss";

const mapCategory = (row) => ({
  id: Number(row.category_id ?? row.id),
  name: String(row.category ?? row.category_name ?? row.name ?? "").trim(),
  image_path: row.image_path ?? null,
  product_count: Number(row.product_count ?? 0),
  supplier_count: Number(row.supplier_count ?? 0),
  buyer_request_count: Number(row.buyer_request_count ?? row.rfq_count ?? 0),
});

const mapSubcategory = (row) => ({
  id: Number(row.sub_category_id ?? row.id),
  name: String(row.sub_category ?? row.name ?? "").trim(),
  image_path: row.image_path ?? null,
  product_count: Number(row.product_count ?? 0),
  supplier_count: Number(row.supplier_count ?? 0),
  buyer_request_count: Number(row.buyer_request_count ?? row.rfq_count ?? 0),
});

const categoryActivityScore = (category) =>
  Number(category?.product_count ?? 0) +
  Number(category?.supplier_count ?? 0) +
  Number(category?.buyer_request_count ?? 0);

const compareCategoriesByActivity = (a, b) => {
  const scoreDiff = categoryActivityScore(b) - categoryActivityScore(a);
  if (scoreDiff !== 0) return scoreDiff;
  return a.name.localeCompare(b.name);
};

const sortCategoriesByActivity = (categories) => [...categories].sort(compareCategoriesByActivity);

const CATEGORIES_PAGE_SIZE = 10;

function extractCategoryRows(response) {
  const envelope = response?.data ?? response ?? {};
  if (Array.isArray(envelope.categories)) return envelope.categories;
  if (Array.isArray(envelope?.data?.categories)) return envelope.data.categories;
  if (Array.isArray(envelope)) return envelope;
  if (Array.isArray(envelope.data)) return envelope.data;
  return [];
}

function extractListRows(response) {
  const payload = response?.data ?? response ?? {};
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
}

function ToggleSwitch({ checked, disabled, onChange, label }) {
  return (
    <label className="sa-toggle" title={label}>
      <input
        type="checkbox"
        className="sa-toggle__input"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        aria-label={label}
      />
      <span className="sa-toggle__track" aria-hidden />
    </label>
  );
}

function SubcategoriesModal({
  open,
  category,
  saving,
  selectedSubIds,
  subcategories,
  loading,
  maxSubcategories,
  onClose,
  onToggleSubcategory,
  onMoveSubcategory,
}) {
  if (!open || !category) return null;

  return (
    <div className="sa-modal-backdrop" role="presentation" onClick={() => !saving && onClose()}>
      <div
        className="sa-modal sa-modal--trading-sub"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sa-fcs-sub-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="sa-tc-modal__head">
          <div>
            <h2 id="sa-fcs-sub-modal-title" className="mca-section-title">
              Sub-categories for {category.name}
            </h2>
            <p className="sa-modal__lead">
              Select up to {maxSubcategories} sub-categories for the home showcase grid.
            </p>
          </div>
          <button type="button" className="sa-tc-modal__close" onClick={onClose} disabled={saving} aria-label="Close">
            <FaTimes aria-hidden />
          </button>
        </div>

        {loading ? (
          <p className="sa-tc-sub-panel__loading">Loading sub-categories…</p>
        ) : subcategories.length ? (
          <ul className="sa-tc-sub-list sa-tc-sub-list--modal">
            {subcategories.map((subcategory) => {
              const isSubOn = selectedSubIds.includes(subcategory.id);
              const subIndex = selectedSubIds.indexOf(subcategory.id);
              const canMoveSub = isSubOn && selectedSubIds.length > 1;
              const imageSrc = subcategory.image_path ? resolvePublicFileUrl(subcategory.image_path) : null;

              return (
                <li
                  key={subcategory.id}
                  className={`sa-tc-sub-list__item${isSubOn ? "" : " sa-tc-sub-list__item--off"}`}
                >
                  <div className="sa-tc-sub-list__row">
                    <span className={`sa-tc-sub-list__order${isSubOn ? "" : " sa-tc-sub-list__order--off"}`}>
                      {isSubOn ? subIndex + 1 : "—"}
                    </span>
                    <div className="sa-tc-sub-list__copy sa-fcs-sub-copy">
                      {imageSrc ? (
                        <span className="sa-fcs-sub-copy__thumb">
                          <img src={imageSrc} alt="" />
                        </span>
                      ) : null}
                      <span className="sa-tc-sub-list__name">{subcategory.name}</span>
                    </div>
                    <div className="sa-tc-sub-list__controls">
                      <ToggleSwitch
                        checked={isSubOn}
                        disabled={saving || (!isSubOn && selectedSubIds.length >= maxSubcategories)}
                        onChange={(checked) => onToggleSubcategory(subcategory.id, checked)}
                        label={`${isSubOn ? "Hide" : "Show"} ${subcategory.name}`}
                      />
                      {isSubOn ? (
                        <div className="sa-sql-tile-list__order-btns">
                          <button
                            type="button"
                            className="sa-sql-order-btn"
                            disabled={!canMoveSub || saving || subIndex === 0}
                            onClick={() => onMoveSubcategory(subcategory.id, "up")}
                            aria-label={`Move ${subcategory.name} up`}
                          >
                            <FaArrowUp aria-hidden />
                          </button>
                          <button
                            type="button"
                            className="sa-sql-order-btn"
                            disabled={!canMoveSub || saving || subIndex === selectedSubIds.length - 1}
                            onClick={() => onMoveSubcategory(subcategory.id, "down")}
                            aria-label={`Move ${subcategory.name} down`}
                          >
                            <FaArrowDown aria-hidden />
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="sa-tc-sub-panel__empty">No sub-categories found for this category.</p>
        )}

        <div className="sa-modal__actions">
          <button type="button" className="mca-btn mca-btn--primary" onClick={onClose} disabled={saving}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SuperAdminFeaturedCategoryShowcasePage() {
  const [section, setSection] = useState(null);
  const [allCategories, setAllCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [description, setDescription] = useState("");
  const [subcategoryIds, setSubcategoryIds] = useState([]);
  const [subcategoryCache, setSubcategoryCache] = useState({});
  const [loadingSubcategories, setLoadingSubcategories] = useState(false);
  const [subcategoryModalOpen, setSubcategoryModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categorySearchQuery, setCategorySearchQuery] = useState("");
  const [visibleCategoryCount, setVisibleCategoryCount] = useState(CATEGORIES_PAGE_SIZE);

  const displayConfig = useMemo(() => {
    if (!section) {
      return resolveFeaturedCategoryShowcaseConfig(null);
    }
    return resolveFeaturedCategoryShowcaseConfig(section.sectionConfig ?? section.section_config);
  }, [section]);

  const maxSubcategories = displayConfig.total_items;
  const selectedCategory = useMemo(
    () => allCategories.find((category) => category.id === selectedCategoryId) ?? null,
    [allCategories, selectedCategoryId],
  );

  const orderedCategoryOptions = useMemo(() => sortCategoriesByActivity(allCategories), [allCategories]);

  const filteredCategoryOptions = useMemo(() => {
    const query = categorySearchQuery.trim().toLowerCase();
    if (!query) return orderedCategoryOptions;
    return orderedCategoryOptions.filter((category) => category.name.toLowerCase().includes(query));
  }, [orderedCategoryOptions, categorySearchQuery]);

  const visibleCategoryOptions = useMemo(
    () => filteredCategoryOptions.slice(0, visibleCategoryCount),
    [filteredCategoryOptions, visibleCategoryCount],
  );
  const hasMoreCategories = visibleCategoryCount < filteredCategoryOptions.length;

  const loadSubcategories = useCallback(async (categoryId) => {
    if (!categoryId || subcategoryCache[categoryId]) return subcategoryCache[categoryId];

    setLoadingSubcategories(true);
    try {
      const response = await CommonService.get(`/master/master-sub-categories/${categoryId}`, {}, { silent: true });
      const rows = extractListRows(response);
      const mapped = sortCategoriesByActivity(rows.map(mapSubcategory).filter((row) => row.id > 0 && row.name));
      setSubcategoryCache((prev) => ({ ...prev, [categoryId]: mapped }));
      return mapped;
    } catch (err) {
      toast.error("Could not load sub-categories.");
      return [];
    } finally {
      setLoadingSubcategories(false);
    }
  }, [subcategoryCache]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sections, categoriesResponse] = await Promise.all([
        fetchHubHomeSections(),
        b2bHubHomeService.getProductCategories(),
      ]);

      const showcaseSection = sections.find(
        (row) => (row.sectionKey || row.section_key) === HUB_HOME_SECTION_KEYS.FEATURED_CATEGORY_SHOWCASE,
      );
      const sectionConfig = showcaseSection?.sectionConfig ?? showcaseSection?.section_config;
      const config = resolveFeaturedCategoryShowcaseConfig(sectionConfig);
      const categoryRows = extractCategoryRows(categoriesResponse);

      setSection(showcaseSection || null);
      setAllCategories(
        sortCategoriesByActivity(categoryRows.map(mapCategory).filter((row) => row.id > 0 && row.name)),
      );
      setSelectedCategoryId(config.category_id);
      setDescription(config.description);
      setSubcategoryIds(config.subcategory_ids);
      setVisibleCategoryCount(CATEGORIES_PAGE_SIZE);
    } catch (err) {
      toast.error(hubHomeSectionExtractMessage(err, "Could not load Featured Category Showcase settings."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setVisibleCategoryCount(CATEGORIES_PAGE_SIZE);
  }, [categorySearchQuery]);

  useEffect(() => {
    if (selectedCategoryId) {
      void loadSubcategories(selectedCategoryId);
    }
  }, [selectedCategoryId, loadSubcategories]);

  const selectCategory = (categoryId) => {
    if (selectedCategoryId !== categoryId) {
      setSelectedCategoryId(categoryId);
      setSubcategoryIds([]);
    }
    setSubcategoryModalOpen(true);
  };

  const openSubcategoriesModal = () => {
    if (!selectedCategoryId) return;
    setSubcategoryModalOpen(true);
  };

  const toggleSubcategory = (subcategoryId, checked) => {
    setSubcategoryIds((prev) => {
      if (checked) {
        if (prev.includes(subcategoryId) || prev.length >= maxSubcategories) return prev;
        return [...prev, subcategoryId];
      }
      return prev.filter((id) => id !== subcategoryId);
    });
  };

  const moveSubcategory = (subcategoryId, direction) => {
    setSubcategoryIds((prev) => {
      const current = [...prev];
      const index = current.indexOf(subcategoryId);
      if (index < 0) return prev;
      const nextIndex = direction === "up" ? index - 1 : index + 1;
      if (nextIndex < 0 || nextIndex >= current.length) return prev;
      [current[index], current[nextIndex]] = [current[nextIndex], current[index]];
      return current;
    });
  };

  const save = async () => {
    if (!section) {
      toast.error("Featured Category Showcase section was not found.");
      return;
    }
    if (!selectedCategoryId) {
      toast.error("Select a category to feature on the home page.");
      return;
    }
    if (!subcategoryIds.length) {
      toast.error("Select at least one sub-category for the showcase grid.");
      return;
    }

    setSaving(true);
    try {
      const updated = await updateHubHomeSection(section.id, {
        section_config: buildFeaturedCategoryShowcaseSectionConfig(
          section.sectionConfig ?? section.section_config,
          {
            category_id: selectedCategoryId,
            description,
            subcategory_ids: subcategoryIds,
          },
        ),
      });
      if (updated) {
        setSection(updated);
        const config = resolveFeaturedCategoryShowcaseConfig(
          updated.sectionConfig ?? updated.section_config,
        );
        setSelectedCategoryId(config.category_id);
        setDescription(config.description);
        setSubcategoryIds(config.subcategory_ids);
        toast.success("Featured Category Showcase updated.");
      }
    } catch (err) {
      toast.error(hubHomeSectionExtractMessage(err, "Could not update Featured Category Showcase."));
      await load();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mca-page super-admin-page sa-sql-page sa-fcs-page">
      <nav className="mca-breadcrumb" aria-label="Breadcrumb">
        <ol className="mca-breadcrumb__list">
          <li>
            <Link to="/super-admin/dashboard">Super Admin</Link>
          </li>
          <li>
            <Link to="/super-admin/master-data/home-section-management">Home Section Management</Link>
          </li>
          <li aria-current="page">Featured Category Showcase</li>
        </ol>
      </nav>

      <div className="sa-sql-page-head">
        <header className="sa-sql-hero">
          <div className="sa-sql-hero__title-row">
            <span className="sa-sql-hero__icon" aria-hidden>
              <FaLayerGroup />
            </span>
            <h1 className="sa-sql-hero__title">Featured Category Showcase</h1>
            <span className="sa-sql-hero__badge">Super Admin</span>
          </div>
          <p className="sa-sql-hero__subtitle">
            Pick one category to highlight below Trending Categories on the home page. Configure up to{" "}
            <strong>{maxSubcategories}</strong> sub-categories ({displayConfig.rows} rows ×{" "}
            {displayConfig.products_per_row} per row) from{" "}
            <Link to="/super-admin/master-data/home-section-management">Home Section Management</Link>.
          </p>
        </header>
        <div className="sa-sql-page-head__actions">
          <Link to="/home" className="mca-btn mca-btn--outline sa-sql-preview-btn" target="_blank" rel="noopener noreferrer">
            <FaEye aria-hidden /> Preview on Home Page
          </Link>
          <button
            type="button"
            className="mca-btn mca-btn--primary sa-sql-save-btn"
            onClick={() => void save()}
            disabled={loading || saving || !section}
          >
            <FaSave aria-hidden /> {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>

      {loading ? (
        <p className="sa-placeholder-copy">Loading…</p>
      ) : !section ? (
        <p className="sa-placeholder-copy">
          Featured Category Showcase section is missing. Restore it from{" "}
          <Link to="/super-admin/master-data/home-section-management">Home Section Management</Link>.
        </p>
      ) : (
        <>
          <section className="sa-fcs-config-card">
            <h2 className="sa-fcs-config-card__title">Showcase copy</h2>
            <label className="mca-field">
              <span className="mca-field__label">Description (optional)</span>
              <textarea
                className="mca-input sa-fcs-description"
                rows={3}
                value={description}
                disabled={saving || !selectedCategoryId}
                placeholder={
                  selectedCategory
                    ? `Discover a wide range of quality ${selectedCategory.name.toLowerCase()} for every space.`
                    : "Select a category first"
                }
                onChange={(e) => setDescription(e.target.value)}
              />
            </label>
          </section>

          <section className="sa-sql-card">
            <div className="sa-sql-card__head sa-tc-card__head">
              <h2 className="sa-sql-card__title">
                Choose featured category — showing {visibleCategoryOptions.length} of {filteredCategoryOptions.length}
                {categorySearchQuery.trim() ? " matching" : ""}, sorted by activity
              </h2>
              <div className="mca-search sa-tc-search">
                <FaSearch className="mca-search__icon" aria-hidden />
                <input
                  type="search"
                  className="mca-input mca-search__input"
                  placeholder="Search categories…"
                  value={categorySearchQuery}
                  onChange={(event) => setCategorySearchQuery(event.target.value)}
                  aria-label="Search marketplace categories"
                />
              </div>
            </div>

            {filteredCategoryOptions.length ? (
            <ul className="sa-sql-tile-list sa-tc-category-list sa-fcs-category-list">
              {visibleCategoryOptions.map((category, index) => {
                const isSelected = selectedCategoryId === category.id;
                const imageSrc = category.image_path ? resolvePublicFileUrl(category.image_path) : null;

                return (
                  <li
                    key={category.id}
                    className={`sa-tc-category-list__item${isSelected ? " sa-fcs-category-list__item--selected" : ""}`}
                  >
                    <div className="sa-tc-category-list__main">
                      <span className="sa-sql-tile-list__order">{index + 1}</span>
                      <span className="sa-tc-category-list__thumb">
                        {imageSrc ? <img src={imageSrc} alt="" /> : <FaBox aria-hidden />}
                      </span>
                      <div className="sa-sql-tile-list__copy">
                        <span className="sa-sql-tile-list__title">{category.name}</span>
                        <span className="sa-sql-tile-list__desc">
                          {category.product_count} products · {category.supplier_count} suppliers ·{" "}
                          {category.buyer_request_count} RFQs
                        </span>
                        {isSelected ? (
                          <span className="sa-fcs-selected-row__meta">
                            {subcategoryIds.length} of {maxSubcategories} sub-categories selected
                          </span>
                        ) : null}
                      </div>
                      <div className="sa-sql-tile-list__controls sa-fcs-category-list__controls">
                        {isSelected ? (
                          <button
                            type="button"
                            className="sa-tc-expand-btn"
                            onClick={openSubcategoriesModal}
                            disabled={saving}
                          >
                            <FaChevronDown aria-hidden />
                            <span>Sub-categories</span>
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className={`sa-fcs-select-btn${isSelected ? " sa-fcs-select-btn--on" : ""}`}
                          disabled={saving}
                          onClick={() => selectCategory(category.id)}
                        >
                          {isSelected ? "Selected" : "Select"}
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
            ) : (
              <p className="sa-tc-search-empty">
                No categories match &ldquo;{categorySearchQuery.trim()}&rdquo;. Try a different search term.
              </p>
            )}
            {hasMoreCategories ? (
              <div className="sa-tc-load-more">
                <button
                  type="button"
                  className="sa-tc-load-more__btn"
                  onClick={() => setVisibleCategoryCount((count) => count + CATEGORIES_PAGE_SIZE)}
                >
                  View More <FaChevronRight aria-hidden />
                </button>
              </div>
            ) : null}
          </section>
        </>
      )}

      <SubcategoriesModal
        open={subcategoryModalOpen && Boolean(selectedCategory)}
        category={selectedCategory}
        saving={saving}
        selectedSubIds={subcategoryIds}
        subcategories={selectedCategoryId ? subcategoryCache[selectedCategoryId] || [] : []}
        loading={loadingSubcategories}
        maxSubcategories={maxSubcategories}
        onClose={() => setSubcategoryModalOpen(false)}
        onToggleSubcategory={toggleSubcategory}
        onMoveSubcategory={moveSubcategory}
      />
    </div>
  );
}
